import { useState } from 'react'
import { Button } from '../catalyst/button'
import { Checkbox, CheckboxField, CheckboxGroup } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { ErrorMessage, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { ApiError } from '../lib/api'
import { areaBody, areaInputValue, orgAreaUnit, toM2 } from '../lib/area'
import { useAuth } from '../lib/auth'
import { useFieldOptions } from '../lib/field-options'
import type { FieldBody, NewFieldBody } from '../lib/fields'
import { formErrors, noErrors, type FormErrors } from '../lib/form-errors'
import type { AreaUnit, Field, IrrigationMethod, ProductionType, WaterSource } from '../lib/types'
import { orgYieldUnit, yieldInputValue, yieldUnitLabel, type YieldUnit } from '../lib/yield'

const IRRIGATION_LABELS: Record<IrrigationMethod, string> = {
  DRIP: 'Drip',
  OVERHEAD_SPRINKLER: 'Overhead sprinkler',
  MICRO_SPRINKLER: 'Micro-sprinkler',
  RAIN_FED: 'Rain-fed (no irrigation)',
}
const WATER_LABELS: Record<WaterSource, string> = {
  WELL: 'Well',
  MUNICIPAL: 'Municipal',
  RIVER_CREEK: 'River, creek or ditch',
  POND_DUGOUT: 'Pond or dugout',
  IRRIGATION_DISTRICT: 'Irrigation district',
  RAINWATER_ONLY: 'Rainwater',
  OTHER: 'Other',
}
const PRODUCTION_LABELS: Record<ProductionType, string> = {
  ORGANIC: 'Organic',
  CONVENTIONAL: 'Conventional',
  TRANSITIONING: 'Transitioning to organic',
}

/** Adds a field (no `field`) or edits one. `onSave` does the request; errors thrown from it show on the inputs. */
export function FieldFormDialog({
  open,
  onClose,
  field,
  onSave,
}: {
  open: boolean
  onClose: () => void
  field?: Field
  onSave: (body: NewFieldBody | FieldBody) => Promise<void>
}) {
  const { org } = useAuth()
  const options = useFieldOptions(org?.id)
  const editing = field !== undefined
  // A field with several varieties is changed variety by variety, which this form does not do yet.
  const oneVariety = !field || field.plantings.length === 1
  const planting = field?.plantings[0]

  const [areaUnit, setAreaUnit] = useState<AreaUnit>(orgAreaUnit(org))
  const [name, setName] = useState(field?.name ?? '')
  const [area, setArea] = useState(field ? areaInputValue(field.areaM2, orgAreaUnit(org)) : '')
  const [variety, setVariety] = useState(oneVariety ? (planting?.variety ?? '') : '')
  const [yearPlanted, setYearPlanted] = useState(oneVariety && planting?.yearPlanted != null ? String(planting.yearPlanted) : '')
  const [irrigation, setIrrigation] = useState<IrrigationMethod[]>(field?.irrigationMethods ?? [])
  const [water, setWater] = useState<WaterSource[]>(field?.waterSources ?? [])
  const [productionType, setProductionType] = useState<ProductionType | ''>(field?.productionType ?? '')
  const [previousCrop, setPreviousCrop] = useState(field?.previousCrop ?? '')
  const yieldDefaultUnit = orgYieldUnit(org)
  const initialYield = field?.expectedYieldKgPerHa == null ? '' : yieldInputValue(field.expectedYieldKgPerHa, yieldDefaultUnit)
  const [expectedYield, setExpectedYield] = useState(initialYield)
  const [yieldUnit, setYieldUnit] = useState<YieldUnit>(yieldDefaultUnit)
  const [notes, setNotes] = useState(field?.notes ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>(noErrors)

  const invalid = (key: string) => key in errors.fields
  const message = (key: string) => (invalid(key) ? <ErrorMessage>{errors.fields[key]}</ErrorMessage> : null)
  const thisYear = new Date().getFullYear()

  function changeAreaUnit(next: AreaUnit) {
    // Switching the unit converts what is typed, so the same ground stays the same ground.
    const typed = Number(area)
    if (typed > 0) setArea(areaInputValue(toM2(typed, areaUnit), next))
    setAreaUnit(next)
  }

  function toggleIrrigation(method: IrrigationMethod, on: boolean) {
    // Rain-fed means no irrigation, so it stands alone.
    if (!on) setIrrigation((prev) => prev.filter((m) => m !== method))
    else setIrrigation((prev) => (method === 'RAIN_FED' ? ['RAIN_FED'] : [...prev.filter((m) => m !== 'RAIN_FED'), method]))
  }

  const toggleWater = (source: WaterSource, on: boolean) => setWater((prev) => (on ? [...prev, source] : prev.filter((s) => s !== source)))

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const parsedArea = Number(area)
    if (!name.trim() || !(parsedArea > 0)) return
    const year = yearPlanted.trim() === '' ? null : Number(yearPlanted)
    const yieldValue = expectedYield.trim() === '' ? null : Number(expectedYield)
    if (Number.isNaN(year) || Number.isNaN(yieldValue)) {
      setErrors({ fields: { [Number.isNaN(year) ? 'yearPlanted' : 'expectedYieldKgPerHa']: 'Enter a number' }, general: null })
      return
    }

    const body: FieldBody = {
      name: name.trim(),
      areaM2: areaBody(parsedArea, areaUnit),
      notes: notes.trim() || null,
      irrigationMethods: irrigation,
      waterSources: water,
      productionType: productionType || null,
      previousCrop: previousCrop.trim() || null,
    }
    if (oneVariety) {
      body.variety = variety.trim() || null
      body.yearPlanted = year
    }
    // An expected yield that was not touched is not sent again: sending it would move it to this season.
    if (yieldValue !== null && (expectedYield !== initialYield || yieldUnit !== yieldDefaultUnit)) body.expectedYieldKgPerHa = { value: yieldValue, unit: yieldUnit }
    else if (yieldValue === null && field?.expectedYieldKgPerHa != null) body.expectedYieldKgPerHa = null

    setSubmitting(true)
    setErrors(noErrors)
    try {
      if (editing) {
        await onSave(body)
      } else {
        // A new field only needs what was filled in.
        await onSave(
          Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null && !(Array.isArray(v) && v.length === 0))) as NewFieldBody,
        )
      }
      onClose()
    } catch (err) {
      const mapped = formErrors(err, editing ? 'Could not save field.' : 'Could not add field.')
      // A duplicate name comes back as a conflict with no field path: it belongs on the name input.
      if (err instanceof ApiError && err.code === 'CONFLICT') setErrors({ fields: { name: err.message }, general: null })
      else setErrors(mapped)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{editing ? 'Edit field' : 'Add field'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {errors.general && <Text className="text-red-600 dark:text-red-400">{errors.general}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Field name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} invalid={invalid('name')} autoFocus required />
              {message('name')}
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField>
                <Label>Area</Label>
                <Input type="number" min="0" step="any" inputMode="decimal" value={area} onChange={(event) => setArea(event.target.value)} invalid={invalid('areaM2')} required />
                {message('areaM2')}
              </FormField>
              <FormField>
                <Label>Unit</Label>
                <Select value={areaUnit} onChange={(event) => changeAreaUnit(event.target.value as AreaUnit)}>
                  <option value="acre">Acres</option>
                  <option value="hectare">Hectares</option>
                </Select>
              </FormField>
            </div>

            {oneVariety ? (
              <div className="grid grid-cols-2 gap-4">
                <FormField>
                  <Label>Variety</Label>
                  <Input value={variety} onChange={(event) => setVariety(event.target.value)} invalid={invalid('variety')} list="field-variety-options" placeholder="e.g. Duke" />
                  <datalist id="field-variety-options">
                    {(options?.varieties ?? []).map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                  {message('variety')}
                </FormField>
                <FormField>
                  <Label>Year planted</Label>
                  <Input type="number" inputMode="numeric" value={yearPlanted} onChange={(event) => setYearPlanted(event.target.value)} invalid={invalid('yearPlanted')} placeholder={String(thisYear - 5)} />
                  {message('yearPlanted')}
                </FormField>
              </div>
            ) : (
              <Text>
                This field has more than one variety ({field?.plantings.map((p) => p.variety ?? 'unnamed').join(', ')}). Changing those isn&apos;t available here yet.
              </Text>
            )}

            <fieldset className="space-y-3">
              <legend className="text-sm/6 font-medium text-zinc-950 dark:text-white">Irrigation</legend>
              <CheckboxGroup>
                {(options?.irrigationMethods ?? []).map((method) => (
                  <CheckboxField key={method}>
                    <Checkbox checked={irrigation.includes(method)} onChange={(on) => toggleIrrigation(method, on)} />
                    <Label>{IRRIGATION_LABELS[method]}</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
              {message('irrigationMethods')}
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm/6 font-medium text-zinc-950 dark:text-white">Water source</legend>
              <CheckboxGroup>
                {(options?.waterSources ?? []).map((source) => (
                  <CheckboxField key={source}>
                    <Checkbox checked={water.includes(source)} onChange={(on) => toggleWater(source, on)} />
                    <Label>{WATER_LABELS[source]}</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
              {message('waterSources')}
            </fieldset>

            <FormField>
              <Label>Production</Label>
              <Select value={productionType} onChange={(event) => setProductionType(event.target.value as ProductionType | '')} invalid={invalid('productionType')}>
                <option value="">Not said</option>
                {(options?.productionTypes ?? []).map((type) => (
                  <option key={type} value={type}>
                    {PRODUCTION_LABELS[type]}
                  </option>
                ))}
              </Select>
              {message('productionType')}
            </FormField>

            <FormField>
              <Label>Previous year&apos;s crop</Label>
              <Input value={previousCrop} onChange={(event) => setPreviousCrop(event.target.value)} invalid={invalid('previousCrop')} />
              {message('previousCrop')}
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField>
                <Label>Expected yield this year</Label>
                <Input type="number" min="0" step="any" inputMode="decimal" value={expectedYield} onChange={(event) => setExpectedYield(event.target.value)} invalid={invalid('expectedYieldKgPerHa')} />
                {message('expectedYieldKgPerHa')}
                {field?.expectedYieldYear != null && expectedYield === initialYield && (
                  <Text>{field.expectedYieldYear < thisYear ? `From ${field.expectedYieldYear}: out of date` : `For ${field.expectedYieldYear}`}</Text>
                )}
              </FormField>
              <FormField>
                <Label>Unit</Label>
                <Select value={yieldUnit} onChange={(event) => setYieldUnit(event.target.value as YieldUnit)}>
                  <option value="lb_ac">{yieldUnitLabel('lb_ac')}</option>
                  <option value="kg_ha">{yieldUnitLabel('kg_ha')}</option>
                </Select>
              </FormField>
            </div>

            <FormField>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} />
              {message('notes')}
            </FormField>
          </FieldGroup>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : editing ? 'Save' : 'Add field'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
